// ============================================================================
// The Supabase client, widened to what mig 470 (RIZ127) added.
//
// WHY THIS EXISTS RATHER THAN AN EDIT TO src/types/database.ts: that file is
// generated by `supabase gen types` and the house rule is never to hand-edit it.
// Regenerated 2026-10-27 after mig 470 applied, it types assign_mode as plain
// text and the two RPCs below with loose arguments (p_offer_ids cannot be null).
//
// The notificationDb.ts / showingIntakeDb.ts pattern: narrow the SCHEMA GENERIC
// for exactly what this feature added, so every row stays fully typed, no call
// site needs a cast, and there is still ONE client instance. Every entry it
// narrows is REPLACED (Omit-then-add), not intersected - an intersection of two
// entries resolves to `never` (or an argument nobody can pass) at the call site.
// ============================================================================
import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Database, Json } from '@/types/database'

type RuleTable = Database['public']['Tables']['lead_routing_rules']

/** mig 470's columns on lead_routing_rules. A type ALIAS (supabase-js needs the implicit index signature). */
export type LeadRoutingGroupColumns = {
  assign_mode: 'agent' | 'round_robin' | 'first_to_claim'
  group_member_ids: string[]
  rr_last_user_id: string | null
  claim_window_minutes: number
  claim_fallback_agent_id: string | null
}

/** One row of lead_claim_offers_for_me(org, ids) - the ALLOW-LIST the database returns. */
export type LeadOfferRow = {
  offer_id: string
  /** open | claimed | expired | withdrawn, or while still open: assigned (by hand) | closing (window over) */
  status: string
  offered_at: string
  expires_at: string
  claim_window_minutes: number
  lead_name: string | null
  lead_source_label: string | null
  inquiry_address: string | null
  inquiry_city: string | null
  inquiry_price: string | null
  lead_side: string | null
  claimed_by: string | null
  claimed_by_name: string | null
  claimed_at: string | null
  resolved_agent_id: string | null
  resolved_agent_name: string | null
  resolution: string | null
  /** Only when the reader may open the contact anyway (theirs now, or the operational tier). */
  contact_id: string | null
  can_claim: boolean
}

// Args as a `type` alias, not an interface: supabase-js types an interface arg as never.
export type LeadOffersArgs = { p_org_id: string; p_offer_ids?: string[] | null }
export type ClaimLeadOfferArgs = { p_offer_id: string }

type LeadClaimSchema = {
  __InternalSupabase: Database['__InternalSupabase']
  public: {
    Views: Database['public']['Views']
    Enums: Database['public']['Enums']
    CompositeTypes: Database['public']['CompositeTypes']
    Functions: Omit<Database['public']['Functions'], 'lead_claim_offers_for_me' | 'claim_lead_offer'> & {
      lead_claim_offers_for_me: {
        Args: LeadOffersArgs
        Returns: LeadOfferRow[]
      }
      claim_lead_offer: {
        Args: ClaimLeadOfferArgs
        Returns: Json
      }
    }
    Tables: Omit<Database['public']['Tables'], 'lead_routing_rules'> & {
      lead_routing_rules: {
        Row: RuleTable['Row'] & LeadRoutingGroupColumns
        Insert: RuleTable['Insert'] & Partial<LeadRoutingGroupColumns>
        Update: RuleTable['Update'] & Partial<LeadRoutingGroupColumns>
        Relationships: RuleTable['Relationships']
      }
    }
  }
}

export const leadClaimDb = supabase as unknown as SupabaseClient<LeadClaimSchema>
